import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CONTACT } from "@/lib/contact";
import { scrollToSection } from "@/components/site/SmoothScroll";
import LanguageSwitch from "@/components/site/LanguageSwitch";
import { useT } from "@/lib/i18n";

const NAV_KEYS = [
  { id: "about", key: "nav.about" },
  { id: "programs", key: "nav.programs" },
  { id: "curriculum", key: "nav.curriculum" },
  { id: "daycare", key: "nav.daycare" },
  { id: "gallery", key: "nav.gallery" },
  { id: "testimonials", key: "nav.parents" },
  { id: "contact", key: "nav.contact" },
];

const Navbar = () => {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    scrollToSection(id);
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl shadow-[0_4px_30px_-12px_rgba(14,165,233,0.25)]"
          : "bg-transparent"
      }`}
    >
      <nav className="mx-auto max-w-7xl px-5 lg:px-8 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <img
            src={CONTACT.logoUrl}
            alt="Little Ark Preschool"
            className="h-12 md:h-14 w-auto object-contain"
          />
        </Link>

        <ul className="hidden lg:flex items-center gap-1">
          {NAV_KEYS.map((it) => (
            <li key={it.id}>
              <button
                data-testid={`nav-${it.id}`}
                onClick={() => scrollTo(it.id)}
                className="px-3 py-2 rounded-full text-sm font-semibold text-slate-700 hover:text-ark-skyDeep hover:bg-sky-50 transition"
              >
                {t(it.key)}
              </button>
            </li>
          ))}
        </ul>

        <div className="hidden lg:flex items-center gap-3">
          <LanguageSwitch />
          <a
            href={`tel:${CONTACT.phoneRaw}`}
            data-testid="nav-call-btn"
            className="text-sm font-semibold text-ark-tealDeep hover:text-ark-teal"
          >
            {CONTACT.phone}
          </a>
          <Button
            data-testid="nav-cta-btn"
            onClick={() => scrollTo("contact")}
            className="rounded-full bg-ark-sky hover:bg-ark-skyDeep text-white font-semibold px-5 shadow-ark-sky"
          >
            {t("nav.bookTour")}
          </Button>
        </div>

        <div className="lg:hidden flex items-center gap-2">
          <LanguageSwitch compact />
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setOpen((v) => !v)}
            className="p-2 rounded-xl bg-white/70 backdrop-blur border border-slate-200"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-slate-100 shadow-lg">
          <ul className="px-5 py-4 space-y-1">
            {NAV_KEYS.map((it) => (
              <li key={it.id}>
                <button
                  data-testid={`mobile-nav-${it.id}`}
                  onClick={() => scrollTo(it.id)}
                  className="w-full text-left px-3 py-3 rounded-xl text-base font-semibold text-slate-700 hover:bg-sky-50"
                >
                  {t(it.key)}
                </button>
              </li>
            ))}
            <li className="pt-2">
              <Button
                data-testid="mobile-cta-btn"
                onClick={() => scrollTo("contact")}
                className="w-full rounded-full bg-ark-sky hover:bg-ark-skyDeep text-white font-semibold"
              >
                {t("nav.bookTour")}
              </Button>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
};

export default Navbar;
